<template>
  <a-modal v-model:open="computedShowModal"
           width="600px"
           :closable="false"
           :footer="null"
           :mask-closable="false"
           :mask-style="{backdropFilter: 'blur(2px)'}"
           :keyboard="false"
  >
    <div class="modal-content-body">
      <div class="search-hospital-header">
        <div class="modal-title">
          <i :class="iconClass"></i> {{ titleText }}
        </div>
        <button
            type="button"
            class="btn-close"
            @click="closeModal"
        >
          <i class="fa-regular fa-x"/>
        </button>
      </div>
      <div class="search-hospital-content">
        <!-- 타입 선택 라디오 버튼 -->
        <div class="type-selector">
          <a-radio-group v-model:value="selectedType" @change="handleTypeChange">
            <a-radio value="HOSPITAL">
              <span class="radio-label">
                <i class="fa-solid fa-hospital"></i> 개별병원 추가
              </span>
            </a-radio>
            <a-radio value="MANUFACTURER">
              <span class="radio-label">
                <i class="fa-solid fa-building-user"></i> 제조사 소속 병원 한번에 추가
              </span>
            </a-radio>
          </a-radio-group>
        </div>
        
        <!-- 제조사 선택 셀렉트 박스 (제조사 타입 선택 시에만 표시) -->
        <div class="manufacturer-selector" v-if="selectedType === 'MANUFACTURER'">
          <a-select
            v-model:value="selectedManufacturerId"
            placeholder="제조사를 선택해주세요"
            :loading="manufacturersLoading"
            class="manufacturer-select"
            @change="handleManufacturerChange"
            :disabled="manufacturersLoading"
            :notFoundContent="manufacturersLoading ? '제조사 목록 로딩 중...' : '제조사가 없습니다'"
          >
            <a-select-option v-for="manufacturer in manufacturers" :key="manufacturer.id" :value="manufacturer.id">
              {{ manufacturer.name }}
            </a-select-option>
          </a-select>
          
          <!-- 제조사 소속 병원 모두 추가 체크박스 UI 개선 -->
          <div class="select-all-container" v-if="selectedManufacturerId">
            <a-checkbox 
              v-model:checked="selectAllManufacturerHospitals"
              @change="handleSelectAllChange"
            >
              <span class="checkbox-label">
                <i class="fa-solid fa-check-double"></i> 선택한 제조사 소속 병원 모두 추가하기
              </span>
            </a-checkbox>
            <div class="info-text" v-if="selectAllManufacturerHospitals">
              <i class="fa-solid fa-circle-info"></i> 
              {{ getManufacturerName() }} 제조사의 모든 병원이 자동으로 추가됩니다. 병원 목록이 많아도 효율적으로 관리할 수 있습니다.
            </div>
          </div>
        </div>
        
        <!-- 검색 입력 -->
        <div class="search-input-container">
          <i class="fa-solid fa-magnifying-glass"/>
          <input
              type="text"
              @keyup.enter="search"
              v-model="searchParam.condition"
              :placeholder="selectedType === 'MANUFACTURER' && selectedManufacturerId ? '선택한 제조사의 병원 검색' : placeholderText"
          >
          <button class="search yellow-button" @click="search">검색</button>
        </div>
        
        <!-- 선택된 병원 정보 표시 -->
        <div class="selected-hospitals-summary" v-if="selectedHospitals.length > 0">
          <div class="selection-info">
            <div class="selection-count">
              <i class="fa-solid fa-check-circle"></i> 
              선택됨: <strong>{{ selectedHospitals.length }}</strong>개
              <span v-if="selectedType === 'HOSPITAL' && isSelectionLimitReached" class="warning-text">
                (최대 선택 수 도달)
              </span>
            </div>
            <a-button type="link" class="clear-button" @click="clearAllSelections">
              모두 해제
            </a-button>
          </div>
          
          <a-alert 
            v-if="selectedType === 'HOSPITAL' && remainingSelections <= 2" 
            type="warning" 
            message="선택 가능한 병원 수가 제한되어 있습니다." 
            show-icon
            banner 
            class="selection-warning"
          />
        </div>
        
        <!-- 로딩 상태 -->
        <div v-if="isLoading" class="loading-container">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-2">데이터를 불러오는 중입니다...</p>
        </div>
        
        <!-- 검색 결과 목록 -->
        <div v-else class="list-container">
          <div class="list-items" v-if="result.list.length > 0">
            <div
                v-for="item in result.list"
                :key="item.id"
                class="list-item"
                :class="{ 
                  'selected': selectedHospitals.includes(item.id),
                  'disabled': isSelectionLimitReached && !selectedHospitals.includes(item.id) && selectedType === 'HOSPITAL'
                }"
                @click="toggleHospital(item)"
            >
              <div class="item-content">
                <div class="item-name">{{ item.name }}</div>
                <div v-if="item.address" class="item-address">{{ item.address }}</div>
              </div>
              <div class="item-right">
                <i class="fa-solid fa-check" v-if="selectedHospitals.includes(item.id)"/>
                <span class="limit-badge" v-else-if="isSelectionLimitReached && selectedType === 'HOSPITAL'">
                  <i class="fa-solid fa-ban"></i>
                </span>
              </div>
            </div>
          </div>
          <no-result
              v-else
              :colspan="10"
              :message="computedNoResultMessage"
              :height="'auto'"
              :noIcon="true"
          />
        </div>
        
        <!-- 페이지네이션 -->
        <div class="pagination-container">
          <pagination
              v-if="!isLoading && result.list.length > 0"
              :page-info="paginationConfig"
              page-name="SearchHospitalModal"
              :pagePerBlock="5"
              @change-page="paginationHandler"
          />
        </div>
        
        <!-- 하단 버튼 영역 -->
        <div class="modal-footer-actions">
          <a-button @click="closeModal">취소</a-button>
          <a-button 
            type="primary" 
            @click="submitHospitals" 
            :disabled="!selectedHospitals.length && !(selectedType === 'MANUFACTURER' && selectAllManufacturerHospitals && selectedManufacturerId)"
            class="yellow-button"
          >
            {{ getSubmitButtonText() }}
          </a-button>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script>
import NoResult from '@/views/components/NoResult.vue';
import Pagination from '@/views/components/Pagination.vue';
import HospitalService from '../service/HospitalService';

export default {
  name: "SearchHospitalModal",
  components: {
    Pagination,
    NoResult,
  },
  emits: ['update:open', 'select-hospitals', 'close-modal'],
  props: {
    open: {
      type: Boolean,
      required: true,
      default: false
    },
    categoryId: {
      type: String,
      required: false,
      default: ''
    },
    type: {
      type: String,
      required: false,
      default: 'HOSPITAL',
      validator: (value) => ['HOSPITAL', 'MANUFACTURER'].includes(value)
    }
  },
  computed: {
    computedShowModal: {
      get() {
        return this.open;
      },
      set(value) {
        this.$emit('update:open', value);
      }
    },
    computedNoResultMessage() {
      return this.searchParam.condition 
        ? `'${this.searchParam.condition}'에 대한 검색 결과가 없습니다.` 
        : '검색 결과가 없습니다.';
    },
    paginationConfig() {
      return {
        currentPage: this.paginationData.currentPage,
        totalPages: this.paginationData.totalPages,
      };
    },
    iconClass() {
      return this.selectedType === 'HOSPITAL' ? 'fa-solid fa-hospital' : 'fa-solid fa-industry';
    },
    titleText() {
      return this.selectedType === 'HOSPITAL' ? '개별 병원 검색' : '제조사 검색';
    },
    placeholderText() {
      return this.selectedType === 'HOSPITAL' ? '병원 이름을 입력해주세요.' : '제조사 이름을 입력해주세요.';
    },
    /**
     * 병원 최대 선택 가능 여부
     */
    isSelectionLimitReached() {
      return this.selectedHospitals.length >= this.selectionLimit;
    },
    
    /**
     * 병원 선택 수 표시
     */
    selectionCountText() {
      return `${this.selectedHospitals.length}/${this.selectionLimit}`;
    },
    
    /**
     * 선택가능한 남은 병원 수
     */
    remainingSelections() {
      return this.selectionLimit - this.selectedHospitals.length;
    },
    
    /**
     * 선택이 가능한지 여부
     */
    canSelectMore() {
      return this.selectedHospitals.length < this.selectionLimit;
    }
  },
  data() {
    return {
      isLoading: false,
      selectedType: 'HOSPITAL', // 기본값은 병원
      searchParam: {
        condition: '',
      },
      paginationData: {
        rowsPerPage: 10,
        currentPage: 1,
        totalPages: 0,
      },
      selectedHospitals: [], // 선택된 병원 ID 목록
      selectedHospitalObjects: {}, // 선택된 병원 객체 정보 (ID를 키로 사용)
      selectionLimit: 10, // 병원 선택 제한
      result: {
        list: [],
        totalDocs: 0,
      },
      selectedManufacturerId: null,
      manufacturers: [],
      manufacturersLoading: false,
      selectAllManufacturerHospitals: true, // 기본값을 true로 변경
    }
  },
  watch: {
    open(newVal) {
      if (newVal === true) {
        // 모달이 열릴 때 초기화
        this.searchParam.condition = '';
        this.paginationData.currentPage = 1;
        this.selectedHospitals = [];
        this.selectedHospitalObjects = {};
        this.result.list = [];
        this.selectedManufacturerId = null;
        this.selectAllManufacturerHospitals = true;
        // 모달 열릴 때 type 프로퍼티 값을 내부 상태로 설정
        this.selectedType = this.type;
        
        // 제조사 타입이면 제조사 목록 로드
        if (this.selectedType === 'MANUFACTURER') {
          this.loadManufacturers();
        }
        
        // 모달이 열리면 자동으로 첫 페이지 결과 로드
        this.$nextTick(() => {
          this.getHospitalList();
        });
      }
    }
  },
  methods: {
    /**
     * 타입 변경 핸들러
     */
    handleTypeChange() {
      // 타입 변경 시 검색 결과 초기화
      this.result.list = [];
      this.result.totalDocs = 0;
      this.paginationData.currentPage = 1;
      this.selectedHospitals = [];
      this.selectedHospitalObjects = {};
      this.selectedManufacturerId = null;
      this.selectAllManufacturerHospitals = true;
      
      // 제조사 타입 선택 시 제조사 목록 로드
      if (this.selectedType === 'MANUFACTURER') {
        this.loadManufacturers();
      }
      
      // 검색 조건이 있으면 자동으로 검색 실행
      if (this.searchParam.condition) {
        this.search();
      }
    },
    
    /**
     * 제조사 목록 로드
     */
    async loadManufacturers() {
      try {
        this.manufacturersLoading = true;
        
        // 제조사 목록 조회 파라미터
        const params = {
          page: 1,
          limit: 100, // 셀렉트 박스에 표시할 최대 개수
          search: 'name',
          condition: '',
          type: 'MANUFACTURER' // 필수: type 파라미터 추가
        };
        
        // 제조사 목록 조회
        const result = await HospitalService.getManufacturerList(this.$apiInstance, params);
        this.manufacturers = result.list;
        
        // 제조사가 있으면 첫번째 항목을 자동 선택
        if (this.manufacturers && this.manufacturers.length > 0) {
          this.selectedManufacturerId = this.manufacturers[0].id;
          
          // 선택된 제조사로 병원 목록 자동 조회
          this.$nextTick(() => {
            this.getHospitalList();
          });
        }
      } catch (error) {
        console.error('제조사 목록 로드 실패:', error);
        this.$message.error('제조사 목록을 불러오는 중 오류가 발생했습니다.');
        this.manufacturers = [];
      } finally {
        this.manufacturersLoading = false;
      }
    },
    
    /**
     * 병원 목록 조회
     */
    async getHospitalList() {
      // 제조사 타입인데 제조사가 선택되지 않은 경우 알림
      if (this.selectedType === 'MANUFACTURER' && !this.selectedManufacturerId) {
        if (this.searchParam.condition) { // 검색어가 있는 경우만 알림 표시
          this.$message.warning('제조사를 먼저 선택해주세요.');
        }
        this.result.list = [];
        this.result.totalDocs = 0;
        this.paginationData.totalPages = 0;
        return;
      }
      
      try {
        this.isLoading = true;
        
        // 검색 파라미터 구성
        const params = HospitalService.getSearchParams({
          currentPage: this.paginationData.currentPage,
          rowsPerPage: this.paginationData.rowsPerPage,
          condition: this.searchParam.condition,
          type: this.selectedType // 내부 상태 사용
        });
        
        // 제조사 타입이고 제조사가 선택되었을 경우 viewManufacturerId 추가
        if (this.selectedType === 'MANUFACTURER' && this.selectedManufacturerId) {
          params.viewManufacturerId = this.selectedManufacturerId;
        }
        
        // 타입에 따라 적절한 API 호출
        let result;
        if (this.selectedType === 'HOSPITAL') {
          // 병원 조회 API 호출
          result = await HospitalService.getHospitalList(this.$apiInstance, params);
        } else {
          // 제조사에 속한 병원 조회 API 호출
          result = await HospitalService.getHospitalList(this.$apiInstance, params);
        }
        
        // 결과 처리
        this.result.list = result.list;
        this.result.totalDocs = result.totalDocs;
        this.paginationData.totalPages = result.totalPages;
      } catch (error) {
        console.error('목록 조회 실패:', error);
        this.$message.error('목록을 불러오는 중 오류가 발생했습니다.');
        this.result.list = [];
        this.result.totalDocs = 0;
        this.paginationData.totalPages = 0;
      } finally {
        this.isLoading = false;
      }
    },
    
    /**
     * 검색 실행
     */
    search() {
      // 제조사 타입인데 제조사가 선택되지 않은 경우 알림
      if (this.selectedType === 'MANUFACTURER' && !this.selectedManufacturerId) {
        this.$message.warning('제조사를 먼저 선택해주세요.');
        return;
      }
      
      this.paginationData.currentPage = 1;
      this.getHospitalList();
    },
    
    /**
     * 페이지네이션 이벤트 처리
     */
    paginationHandler(childPassParam) {
      this.paginationData.currentPage = childPassParam.page;
      this.getHospitalList();
    },
    
    /**
     * 병원 선택/해제 토글
     */
    toggleHospital(hospital) {
      // 이미 선택된 병원인지 확인
      if (this.selectedHospitals.includes(hospital.id)) {
        // 이미 선택된 병원이면 선택 취소
        this.selectedHospitals = this.selectedHospitals.filter(id => id !== hospital.id);
        
        // 객체 정보에서도 제거
        const updatedObjects = { ...this.selectedHospitalObjects };
        delete updatedObjects[hospital.id];
        this.selectedHospitalObjects = updatedObjects;
      } else {
        // 선택 제한 확인 (HOSPITAL 타입일 때만 적용)
        if (this.selectedType === 'HOSPITAL' && this.isSelectionLimitReached) {
          this.$message.warning({
            content: '최대 10개까지만 선택할 수 있습니다. 더 많은 병원을 관리하려면 모달을 닫고 \'그룹 병원 관리\' 모드를 사용하세요.',
            duration: 5
          });
          return;
        }
        
        // 새로 선택한 병원 추가
        this.selectedHospitals.push(hospital.id);
        
        // 객체 정보 저장
        this.selectedHospitalObjects = {
          ...this.selectedHospitalObjects,
          [hospital.id]: {
            id: hospital.id,
            name: hospital.name,
            address: hospital.address,
            order: this.selectedHospitals.length,
            active: true
          }
        };
        
        // 선택 제한에 근접했을 때 알림 (7개 이상 선택했을 때)
        if (this.selectedType === 'HOSPITAL' && this.selectedHospitals.length === 7) {
          this.$message.info({
            content: `앞으로 ${this.selectionLimit - this.selectedHospitals.length}개 더 선택할 수 있습니다. 더 많은 병원을 관리하려면 '그룹 병원 관리' 모드를 사용하세요.`,
            duration: 5
          });
        }
      }
    },
    
    /**
     * 선택한 병원 제출
     */
    submitHospitals() {
      // 제조사 타입이고 모두 추가 체크박스가 체크된 경우
      if (this.selectedType === 'MANUFACTURER' && this.selectAllManufacturerHospitals) {
        if (!this.selectedManufacturerId) {
          this.$message.warning('제조사를 선택해 주세요.');
          return;
        }
        
        // 선택된 제조사의 모든 병원을 추가하는 특별 마커 전달
        this.$emit('select-hospitals', [{
          id: 'ALL_' + this.selectedManufacturerId,
          name: this.getManufacturerName(),
          isAllHospitals: true,
          manufacturerId: this.selectedManufacturerId
        }]);
        this.computedShowModal = false;
        return;
      }
      
      // 일반적인 선택 병원 처리
      if (!this.selectedHospitals.length) {
        const message = this.selectedType === 'HOSPITAL' 
          ? '병원을 선택해 주세요.' 
          : '병원을 선택하거나 모두 추가 옵션을 선택해 주세요.';
        this.$message.warning(message);
        return;
      }
      
      // 선택된 병원 객체들을 배열로 변환하여 전달
      const selectedHospitalsList = Object.values(this.selectedHospitalObjects);
      
      // 선택된 병원 전달
      this.$emit('select-hospitals', selectedHospitalsList);
      this.computedShowModal = false;
    },
    
    /**
     * 모달 닫기
     */
    closeModal() {
      this.$emit('close-modal');
      this.computedShowModal = false;
    },
    
    /**
     * 제조사 변경 처리
     */
    handleManufacturerChange() {
      // 제조사 변경 시 검색 결과 초기화
      this.result.list = [];
      this.result.totalDocs = 0;
      this.paginationData.currentPage = 1;
      this.selectedHospitals = [];
      this.selectedHospitalObjects = {};
      
      // 체크박스 기본값을 체크 상태로 설정
      this.selectAllManufacturerHospitals = true;
      
      // 제조사 변경 시 자동으로 병원 목록 조회
      this.getHospitalList();
    },
    
    /**
     * 선택된 병원 초기화
     */
    clearAllSelections() {
      this.selectedHospitals = [];
      this.selectedHospitalObjects = {};
      if (this.selectedType === 'MANUFACTURER') {
        this.selectAllManufacturerHospitals = false;
      }
    },
    
    /**
     * 제조사 소속 병원 모두 추가 처리
     */
    handleSelectAllChange() {
      if (this.selectAllManufacturerHospitals) {
        // 모두 추가 체크박스 선택 시 기존 선택된 병원 초기화
        this.selectedHospitals = [];
        this.selectedHospitalObjects = {};
      }
    },
    
    /**
     * 선택한 제조사 이름 가져오기
     */
    getManufacturerName() {
      const manufacturer = this.manufacturers.find(m => m.id === this.selectedManufacturerId);
      return manufacturer ? manufacturer.name : '';
    },
    
    /**
     * 모달 제출 버튼 텍스트 가져오기
     */
    getSubmitButtonText() {
      if (this.selectedType === 'MANUFACTURER' && this.selectAllManufacturerHospitals && this.selectedManufacturerId) {
        return `${this.getManufacturerName()} 소속 모든 병원 추가`;
      } else if (this.selectedType === 'HOSPITAL') {
        return `선택한 병원 ${this.selectedHospitals.length}개 추가`;
      } else if (this.selectedType === 'MANUFACTURER') {
        return `제조사 소속 병원 ${this.selectedHospitals.length}개 추가`;
      }
      return '제출';
    },
  }
};
</script>

<style scoped lang="scss">
@import '../styles/variables';

.modal-content-body {
  padding: $spacing-md;
  position: relative;
  display: flex;
  flex-direction: column;
}

.search-hospital-header {
  @include flex-between;
  margin-bottom: $spacing-md;
  
  .modal-title {
    font-size: $font-size-lg;
    font-weight: $font-weight-bold;
    
    i {
      margin-right: $spacing-xs;
      color: $yellow-button-color;
    }
  }
  
  .btn-close {
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: $font-size-md;
    transition: color 0.2s;
    
    &:hover {
      color: $danger-color;
    }
  }
}

.search-hospital-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.type-selector {
  width: 100%;
  margin-bottom: $spacing-md;
  display: flex;
  justify-content: flex-start;
  padding: 0 $spacing-xs;
  flex-wrap: wrap;
  
  :deep(.ant-radio-wrapper) {
    margin-right: $spacing-lg;
    margin-bottom: $spacing-xs;
    font-size: $font-size-md;
    
    .ant-radio-checked .ant-radio-inner {
      border-color: $yellow-button-color;
      
      &::after {
        background-color: $yellow-button-color;
      }
    }
    
    &:hover .ant-radio-inner {
      border-color: $yellow-button-color;
    }
    
    .radio-label {
      display: flex;
      align-items: center;
      
      i {
        margin-right: $spacing-xs;
        color: $yellow-button-color;
      }
    }
  }
}

.manufacturer-selector {
  width: 100%;
  margin-bottom: $spacing-md;
  
  :deep(.manufacturer-select) {
    width: 100%;
    height: 40px;
    
    .ant-select-selector {
      height: 40px !important;
      display: flex;
      align-items: center;
      padding: 0 $spacing-sm 0 $spacing-md;
      border-radius: $border-radius-md;
      border: 1px solid $border-color;
      background: $bg-light;
    }
    
    &:hover .ant-select-selector {
      border-color: $yellow-button-color;
    }
    
    &.ant-select-focused .ant-select-selector {
      border-color: $yellow-button-color;
      box-shadow: 0 0 0 2px rgba($yellow-button-color, 0.2);
    }
    
    // 로딩 상태 스타일
    .ant-select-arrow .anticon-loading {
      color: $yellow-button-color;
    }
  }
}

.search-input-container {
  position: relative;
  width: 100%;
  margin-bottom: $spacing-lg;
  
  i {
    position: absolute;
    left: $spacing-md;
    top: 50%;
    transform: translateY(-50%);
    color: $text-light;
  }
  
  input {
    width: 100%;
    height: 40px;
    padding: $spacing-sm 110px $spacing-sm 38px;
    border: 1px solid $border-color;
    border-radius: $border-radius-md;
    background: $bg-light;
    
    &:focus {
      outline: none;
      border-color: $yellow-button-color;
      box-shadow: 0 0 0 2px rgba($yellow-button-color, 0.2);
    }
  }
  
  button.search {
    position: absolute;
    right: 1px;
    top: 50%;
    transform: translateY(-50%);
    border: none;
    border-radius: $border-radius-sm;
    padding: $spacing-xs $spacing-md;
    cursor: pointer;
    font-weight: $font-weight-medium;
    
    &:disabled {
      background-color: #d9d9d9 !important;
      border-color: #d9d9d9 !important;
      color: rgba(0, 0, 0, 0.25) !important;
      cursor: not-allowed;
    }
  }
}

.selected-hospitals-summary {
  width: 100%;
  margin-bottom: $spacing-md;
  
  .selection-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: $spacing-xs;
    
    .selection-count {
      font-size: $font-size-md;
      font-weight: $font-weight-medium;
      color: $text-dark;
      
      i {
        margin-right: $spacing-xs;
        color: $yellow-button-color;
      }
      
      strong {
        color: $yellow-button-color;
        font-weight: bold;
      }
      
      .warning-text {
        color: #fa8c16;
        font-size: $font-size-sm;
        margin-left: $spacing-xs;
      }
    }
    
    .clear-button {
      padding: 0;
      color: $text-muted;
      
      &:hover {
        color: $text-dark;
      }
    }
  }
  
  .selection-warning {
    margin-top: $spacing-xs;
    
    :deep(.ant-alert-warning) {
      border-color: #ffe58f;
      background-color: #fffbe6;
      
      .ant-alert-message {
        color: #d48806;
      }
      
      .anticon {
        color: #faad14;
      }
    }
  }
}

.list-container {
  width: 100%;
  max-height: 450px;
  overflow-y: auto;
  margin-bottom: $spacing-md;
  border-radius: $border-radius-md;
  border: 1px solid $border-color;
}

.list-items {
  overflow: hidden;
}

.list-item {
  padding: $spacing-md;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid $border-color;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: $bg-light-hover;
  }
  
  &.selected {
    background-color: rgba($yellow-button-color, 0.2);
    border-left: 4px solid $yellow-button-color;
    
    .item-name {
      font-weight: 600;
      color: #000;
    }
  }
  
  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      background-color: $bg-light;
    }
  }
  
  .item-content {
    flex: 1;
    overflow: hidden;
    
    .item-name {
      font-weight: $font-weight-medium;
      @include truncate;
    }
    
    .item-address {
      font-size: $font-size-sm;
      color: $text-muted;
      margin-top: $spacing-xs;
      @include truncate;
    }
  }
  
  .item-right {
    color: $yellow-button-color;
    margin-left: $spacing-md;
    width: 24px;
    text-align: center;
    
    .limit-badge {
      color: #ff4d4f;
      font-size: 12px;
    }
  }
}

.loading-container {
  padding: $spacing-xl;
  text-align: center;
  color: $secondary-color;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.pagination-container {
  width: 100%;
  margin-bottom: $spacing-md;
}

.modal-footer-actions {
  display: flex;
  width: 100%;
  justify-content: flex-end;
  margin-top: $spacing-md;
  gap: $spacing-sm;
}

// 노란색 버튼 스타일 오버라이드
:deep(.yellow-button) {
  background-color: $yellow-button-color !important;
  border-color: $yellow-button-color !important;
  color: $text-dark !important;
  
  &:hover {
    background-color: $yellow-button-color-hover !important;
    border-color: $yellow-button-color-hover !important;
  }
  
  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

// 반응형 디자인
@media (max-width: 576px) {
  .search-input-container {
    input {
      padding-right: 80px;
    }
    
    button.search {
      padding: $spacing-xs $spacing-sm;
      font-size: $font-size-xs;
    }
  }
  
  .list-item {
    padding: $spacing-sm;
  }
}
</style>
